import { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import FeaturedCrossingCardPreview from './FeaturedCrossingCardPreview';
import './FeaturedCrossingCardPreview.css';

const PREVIEW_LANG = {
  es: {
    open: 'ABIERTO',
    closed: 'CERRADO',
    not_available: 'N/D',
    no_wait: 'Sin espera',
    no_lines: 'Sin líneas',
    line_label: 'línea',
    lines_label: 'líneas',
    trend_rising: 'Aumento',
    trend_falling: 'Bajando',
    trend_stable: 'Estable',
    vehicular_crossing: 'CRUCE VEHICULAR',
    pedestrian_crossing_label: 'CRUCE PEATONAL',
    commercial_crossing: 'CRUCE COMERCIAL',
    lane_standard: 'Estándar',
    lane_ready: 'Ready Lane',
    lane_sentri: 'SENTRI',
    lane_fast: 'FAST',
    garita_prefix: 'GARITA',
  },
  en: {
    open: 'OPEN',
    closed: 'CLOSED',
    not_available: 'N/A',
    no_wait: 'No wait',
    no_lines: 'No lanes',
    line_label: 'lane',
    lines_label: 'lanes',
    trend_rising: 'Rising',
    trend_falling: 'Falling',
    trend_stable: 'Stable',
    vehicular_crossing: 'VEHICLE CROSSING',
    pedestrian_crossing_label: 'PEDESTRIAN CROSSING',
    commercial_crossing: 'COMMERCIAL CROSSING',
    lane_standard: 'Standard',
    lane_ready: 'Ready Lane',
    lane_sentri: 'SENTRI',
    lane_fast: 'FAST',
    garita_prefix: 'CROSSING',
  },
};

/** Deterministic mock kits so every card looks good (mix of green / amber / mild red). */
const MOCK_KITS = [
  {
    modes: ['passenger', 'pedestrian', 'commercial'],
    lanes: {
      passenger: [
        { type: 'standard', delay: 125, lines: 5, trend: 'falling' },
        { type: 'sentri', delay: 20, lines: 5, trend: 'rising' },
        { type: 'ready', delay: 105, lines: 5, trend: 'stable' },
      ],
      pedestrian: [{ type: 'standard', delay: 60, lines: 3, trend: 'stable' }],
      commercial: [{ type: 'standard', delay: 45, lines: 2, trend: 'falling' }],
    },
  },
  {
    modes: ['passenger', 'pedestrian'],
    lanes: {
      passenger: [
        { type: 'standard', delay: 55, lines: 4, trend: 'rising' },
        { type: 'sentri', delay: 15, lines: 3, trend: 'stable' },
        { type: 'ready', delay: 35, lines: 4, trend: 'falling' },
      ],
      pedestrian: [{ type: 'standard', delay: 25, lines: 2, trend: 'falling' }],
    },
  },
  {
    modes: ['pedestrian'],
    lanes: {
      pedestrian: [{ type: 'standard', delay: 40, lines: 2, trend: 'stable' }],
    },
  },
  {
    modes: ['passenger', 'pedestrian'],
    lanes: {
      passenger: [
        { type: 'standard', delay: 90, lines: 6, trend: 'stable' },
        { type: 'sentri', delay: 18, lines: 4, trend: 'falling' },
        { type: 'ready', delay: 50, lines: 3, trend: 'rising' },
      ],
      pedestrian: [{ type: 'standard', delay: 55, lines: 3, trend: 'rising' }],
    },
  },
];

function formatFragmentLabel(fragment) {
  const words = String(fragment || '')
    .replace(/^#/, '')
    .replace(/[^a-zA-Z0-9À-ÿ]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function resolveCityLabel(item, crossing) {
  if (item?.use_fragment_port_reference === false) {
    return String(item.custom_city_label || '').trim();
  }
  const fragment =
    (crossing && typeof crossing === 'object'
      ? crossing.urlFragment || crossing.key
      : '') ||
    item.crossing_key ||
    '';
  return formatFragmentLabel(fragment);
}

function pickCrossing(page, crossingName, crossingKey) {
  const list = page?.crossing_names_section?.crossings_names || [];
  return (
    list.find((c) => {
      const name = typeof c === 'string' ? c : c?.name;
      const key = typeof c === 'object' ? c?.urlFragment || c?.key : name;
      return (
        name === crossingName ||
        key === crossingKey ||
        key === crossingName
      );
    }) || null
  );
}

function buildMockCrossing(base, cardIndex = 0) {
  const kit = MOCK_KITS[cardIndex % MOCK_KITS.length];
  const name = base?.name || 'Crossing name';
  const description = base?.description || 'Toward destination city';

  const trend = {};
  const laneGroups = kit.modes.map((mode) => {
    const laneTypes = (kit.lanes[mode] || []).map((lane) => {
      trend[lane.type] = { status: lane.trend };
      return {
        type: lane.type,
        delay_minutes: lane.delay,
        delay_minutes_original: lane.delay,
        lanes_open: lane.lines,
        operational_status: 'Open',
      };
    });
    return {
      transport_mode: mode,
      maximum_lanes: null,
      lane_types: laneTypes,
    };
  });

  const visibility = {};
  kit.modes.forEach((mode) => {
    visibility[mode] = (kit.lanes[mode] || []).map((l) => l.type);
  });

  return {
    ...base,
    name,
    description,
    ports: [
      {
        port_number: 'mock',
        visibility,
        wait_time: {
          port_status: 'Open',
          hours: '24 hrs/day',
          lanes: laneGroups,
        },
        trend,
      },
    ],
  };
}

/**
 * Admin preview of home featured cards.
 * Uses mock wait times (layout QA) — live data only on the public Next app.
 */
export default function FeaturedCardsPreview({
  copy,
  items = [],
  entryPages = [],
  lang = 'es',
}) {
  const [hydrated, setHydrated] = useState([]);
  const [loading, setLoading] = useState(false);
  const labels = PREVIEW_LANG[lang] || PREVIEW_LANG.es;

  const validItems = useMemo(
    () => (items || []).filter((i) => i.entry_page_id && i.crossing_name),
    [items]
  );

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!validItems.length) {
        setHydrated([]);
        return;
      }
      setLoading(true);
      try {
        const uniqueIds = [
          ...new Set(validItems.map((i) => String(i.entry_page_id))),
        ];
        const pagesResults = await Promise.all(
          uniqueIds.map((id) =>
            api.get(`/entry_pages/${id}`).then((r) => r.data).catch(() => null)
          )
        );

        const pageById = {};
        uniqueIds.forEach((id, idx) => {
          if (pagesResults[idx]) pageById[id] = pagesResults[idx];
        });

        const cards = validItems.map((item, index) => {
          const summary = entryPages.find(
            (p) => String(p._id) === String(item.entry_page_id)
          );
          const page = pageById[String(item.entry_page_id)] || summary;
          const rawCrossing = pickCrossing(
            page,
            item.crossing_name,
            item.crossing_key
          );
          const crossingObj =
            typeof rawCrossing === 'string'
              ? { name: rawCrossing, description: '', ports: [] }
              : rawCrossing || {
                  name: item.crossing_name,
                  description: '',
                  ports: [],
                };

          return {
            item,
            page,
            crossing: buildMockCrossing(crossingObj, index),
            image: page?.image || null,
            cityLabel: resolveCityLabel(item, crossingObj),
            departureCity: item.city_label || '',
            href: page?.full_slug || item.entry_page_slug || '#',
          };
        });

        if (!cancelled) setHydrated(cards);
      } catch (e) {
        console.error(e);
        if (!cancelled) setHydrated([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [validItems, entryPages]);

  return (
    <div className="fcc-section">
      <div className="fcc-section__frame">
        <div className="fcc-section__eyebrow">{copy?.eyebrow}</div>
        <h2 className="fcc-section__title">{copy?.title}</h2>
        <p className="fcc-section__desc">{copy?.description}</p>

        {loading && (
          <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.5rem' }} />
            <div className="mt-2">Loading card preview…</div>
          </div>
        )}

        {!loading && hydrated.length === 0 && (
          <div
            style={{
              padding: 24,
              background: '#fff',
              borderRadius: 12,
              color: '#6b7280',
              textAlign: 'center',
            }}
          >
            Add at least one complete card (city + entry page + port) to preview.
          </div>
        )}

        {!loading && hydrated.length > 0 && (
          <div className="fcc-section__grid">
            {hydrated.map((card) => (
              <FeaturedCrossingCardPreview
                key={card.item.id}
                crossing={card.crossing}
                cityLabel={card.cityLabel}
                departureCity={card.departureCity}
                image={card.image}
                labels={labels}
                href={card.href}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
